import { useState, type FormEvent } from 'react';
import { getErrorMessage } from '../api/client';
import { createIngredient } from '../api/inventory';
import type { Ingredient, InventoryUnit } from '../types/inventory';
import { Modal } from './Modal';
import { FooterReason } from './capture';
import { ActionBar } from './ui/ActionBar';

// Fase 26A: `isProposal` (opcional) sirve cuando un EMPLOYEE propone un insumo — el backend lo crea
// como PENDING_APPROVAL. Solo ajusta textos/expectativa; los demás usos (OWNER/MANAGER) no pasan el prop.
export function IngredientQuickCreateModal({ units, onClose, onCreated, isProposal = false }: { units: InventoryUnit[]; onClose: () => void; onCreated: (ingredient: Ingredient) => void; isProposal?: boolean }) {
  // ZTV-13: la unidad base NO trae default — el primero alfabético era "Galón" y producía insumos
  // mal medidos sin que nadie lo eligiera. Un default solo debe existir si es razonablemente
  // correcto; aquí no hay inferencia segura, así que la persona la escoge (el botón explica qué falta).
  const [form, setForm] = useState({ name: '', sku: '', category: '', baseUnitId: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    setError(undefined);
    try {
      const { ingredient } = await createIngredient({ name: form.name.trim(), sku: form.sku.trim() || null, category: form.category.trim() || null, baseUnitId: form.baseUnitId });
      onCreated(ingredient);
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible crear el insumo.'));
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    title={isProposal ? 'Proponer insumo' : 'Nuevo insumo'}
    description={isProposal ? 'Se enviará a aprobación de un dueño o gerente; podrás usarlo en esta compra mientras tanto.' : 'Se agregará al catálogo y quedará disponible de inmediato en este formulario.'}
    isBusy={isSaving}
    onClose={onClose}
    // ZTV-07/ZTV-13: patrón Ticket Mixto — el botón deshabilitado explica qué falta.
    // P-078 (B · H): el motivo en su renglón (antes era una celda junto a Cancelar) y la primaria a todo el ancho.
    footer={<ActionBar aria-label="Acciones del insumo" note={!isSaving && (!form.name.trim() || !form.baseUnitId) ? <FooterReason>{!form.name.trim() ? 'Escribe el nombre del insumo.' : 'Elige la unidad en que lo mides (kg, pieza, litro…).'}</FooterReason> : undefined} primary={<button className="button button--primary" disabled={isSaving || !form.name.trim() || !form.baseUnitId} form="ingredient-quick-create-form" type="submit">{isSaving ? (isProposal ? 'Enviando...' : 'Creando...') : isProposal ? 'Enviar propuesta' : 'Crear insumo'}</button>}>
      <button className="button button--ghost" disabled={isSaving} onClick={onClose} type="button">Cancelar</button>
    </ActionBar>}
  >
    <form id="ingredient-quick-create-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="alert alert--error">{error}</div>}
      {isProposal && <div className="alert alert--info">Esta propuesta quedará pendiente de aprobación. La entrada al inventario solo se registrará cuando un dueño o gerente la apruebe.</div>}
      <div className="form-grid form-grid--compact">
        <label className="field"><span>Nombre</span><input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
        <label className="field"><span>SKU opcional</span><input value={form.sku} onChange={(event) => setForm({ ...form, sku: event.target.value })} /></label>
        <label className="field"><span>Unidad base</span><select required value={form.baseUnitId} onChange={(event) => setForm({ ...form, baseUnitId: event.target.value })}><option value="">¿En qué unidad lo mides?</option>{units.filter((unit) => unit.isActive).map((unit) => <option key={unit.id} value={unit.id}>{unit.name} ({unit.symbol})</option>)}</select><small>Cómo mides este insumo al comprarlo y usarlo (ej. kilogramo, pieza, litro).</small></label>
        <label className="field"><span>Categoría opcional</span><input value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })} /></label>
      </div>
    </form>
  </Modal>;
}
